// src/Admin/TeacherProfile.js
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faChalkboardUser,
  faEnvelope,
  faUser,
  faCakeCandles,
  faCalendarWeek,
  faCalendarCheck,
  faUserGraduate,
  faWallet,
  faChartLine,
  faNoteSticky,
  faPenToSquare,
  faKey,
  faPowerOff,
  faTriangleExclamation,
  faCircleCheck,
  faUserSlash,
  faGlobe,
  faSackDollar,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import EditTeacherInfo from './EditTeacherInfo';
import ResetPassword from './ResetPassword';
import LiveSchedule from '../Teacher/LiveSchedule';
import PaidAvailability from '../Teacher/PaidAvailability';
import Earnings from './Earnings';
import TeacherPay from './TeacherPay';
import StudentsList from './MyStudents';
import UserTrackingTab from './UserTrackingTab';
import 'flag-icons/css/flag-icons.min.css';
import { countryFromTz, ianaOf, cityOf, localTimeIn, offsetLabel } from './Studentzone';
import BadgeShelf from '../components/Competitions/BadgeShelf';
import './TeacherProfile.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const TABS = [
  { key: 'calendar', label: 'Live calendar', icon: faCalendarWeek },
  { key: 'paid', label: 'Paid availability', icon: faCalendarCheck },
  { key: 'students', label: 'Students', icon: faUserGraduate },
  { key: 'earnings', label: 'Earnings', icon: faWallet },
  { key: 'pay', label: 'Pay & stats', icon: faSackDollar },
  { key: 'tracking', label: 'Activity', icon: faChartLine },
  { key: 'note', label: 'Note', icon: faNoteSticky },
  { key: 'edit', label: 'Edit info', icon: faPenToSquare },
  { key: 'reset', label: 'Reset password', icon: faKey },
];

const digits = (p) => String(p || '').replace(/\D/g, '');
const waUrl = (phone) => `https://api.whatsapp.com/send/?phone=${digits(phone)}&text=&type=phone_number&app_absent=0`;

const ageOf = (d) => {
  const t = new Date();
  let a = t.getFullYear() - d.getFullYear();
  const m = t.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a -= 1;
  return a;
};

const Photo = ({ src }) => {
  const [broken, setBroken] = useState(false);
  return (
    <span className="rmatp-photo">
      {src && !broken ? <img src={src} alt="" onError={() => setBroken(true)} /> : <FontAwesomeIcon icon={faChalkboardUser} />}
    </span>
  );
};

/* The teacher's clock: flag, city, local time (ticks every 30 seconds) and UTC offset */
const TeacherClock = ({ tz, hours }) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  const zone = ianaOf(tz);
  const time = localTimeIn(zone, hours, now);
  if (!time) return null;
  const flag = countryFromTz(zone);
  const utc = offsetLabel(zone, hours);
  const city = cityOf(zone) || 'Local time';
  return (
    <span className="rmatp-zone" title={`${city}${utc ? ` · ${utc}` : ''}`}>
      {flag ? <span className={`fi fi-${flag}`} role="img" aria-label={city} /> : <FontAwesomeIcon icon={faGlobe} />}
      <span>
        {city} · <b>{time}</b>
        {utc && <span className="rmatp-dim"> · {utc}</span>}
      </span>
    </span>
  );
};

const TeacherProfile = () => {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const activeTab = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'calendar';

  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [reload, setReload] = useState(0);

  const [tracking, setTracking] = useState({ loading: false, data: null });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [toast, setToast] = useState('');

  // same shape as before ({ id }), kept stable so the tabs don't reload on every render
  const userData = useMemo(() => ({ id }), [id]);

  const setTab = (key) => {
    const next = new URLSearchParams(params);
    if (key === 'calendar') next.delete('tab');
    else next.set('tab', key);
    setParams(next, { replace: true });
  };

  // ---------- Teacher (same endpoint) ----------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/teacherprofile/${id}`, auth());
        if (cancelled) return;
        setTeacher(res.data || null);
        setNotFound(!res.data);
        setError('');
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404) setNotFound(true);
        else setError(err.response?.data?.error || 'We couldn’t load this teacher.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, reload]);

  // ---------- Activity tracking, loaded when its tab opens (same endpoint) ----------
  useEffect(() => {
    if (activeTab !== 'tracking') return undefined;
    let cancelled = false;
    setTracking({ loading: true, data: null });
    (async () => {
      try {
        const res = await axios.get(`${API}/api/user-tracking/teacher/${id}`, auth());
        if (!cancelled) setTracking({ loading: false, data: res.data?.data ?? null });
      } catch (err) {
        console.error('Error fetching tracking data:', err);
        if (!cancelled) setTracking({ loading: false, data: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, id]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!confirmOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && !statusSaving && setConfirmOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmOpen, statusSaving]);

  // ---------- Active / inactive (same endpoint + body) ----------
  const changeStatus = async () => {
    const next = !teacher.IsActive;
    setStatusSaving(true);
    setStatusError('');
    try {
      await axios.put(`${API}/api/admin/teacherprofile/${id}/status`, { isActive: next }, auth());
      setTeacher((t) => ({ ...t, IsActive: next }));
      setConfirmOpen(false);
      setToast(next ? 'Teacher is active again.' : 'Teacher set as inactive.');
    } catch (err) {
      setStatusError(err.response?.data?.error || 'We couldn’t change the status. Please try again.');
    } finally {
      setStatusSaving(false);
    }
  };

  /* ---------- States ---------- */
  if (loading && !teacher) {
    return (
      <div className="rmatp" aria-busy="true">
        <div className="rmatp-inner">
          <div className="rmatp-skel rmatp-skel--head" />
          <div className="rmatp-skel rmatp-skel--bar" />
          <div className="rmatp-skel rmatp-skel--body" />
        </div>
      </div>
    );
  }

  if (error || notFound || !teacher) {
    return (
      <div className="rmatp">
        <div className="rmatp-inner">
          <Link className="rmatp-back" to="/Admin/users">
            <FontAwesomeIcon icon={faArrowLeft} /> All teachers
          </Link>
          <div className="rmatp-empty">
            <FontAwesomeIcon icon={error ? faTriangleExclamation : faUserSlash} className={error ? 'is-error' : ''} />
            <p>{error || 'Teacher not found.'}</p>
            {error && (
              <button type="button" className="rmatp-btn" onClick={() => setReload((n) => n + 1)}>
                Try again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const name = [teacher.FirstName, teacher.LastName].filter(Boolean).join(' ') || 'Teacher';
  const bday = teacher.Birthday ? new Date(teacher.Birthday) : null;
  const bdayOk = bday && !Number.isNaN(bday.getTime());
  const active = !!teacher.IsActive;
  const firstName = teacher.FirstName ? teacher.FirstName.charAt(0).toUpperCase() + teacher.FirstName.slice(1) : 'this teacher';

  return (
    <div className="rmatp">
      <div className="rmatp-inner">
        <Link className="rmatp-back" to="/Admin/users">
          <FontAwesomeIcon icon={faArrowLeft} /> All teachers
        </Link>

        {/* ---------- Header ---------- */}
        <header className="rmatp-hero">
          <span className="rmatp-kind" aria-hidden="true">Teacher</span>
          <Photo src={teacher.Image} />
          <div className="rmatp-who">
            <span className="rmatp-eyebrow">Teacher</span>
            <h1>{name}</h1>
            <div className="rmatp-tags">
              <span className={`rmatp-pill ${active ? 'is-good' : 'is-bad'}`}>
                <i /> {active ? 'Active' : 'Inactive'}
              </span>
              {(teacher.Timezone || teacher.TimeHours != null) && <TeacherClock tz={teacher.Timezone} hours={teacher.TimeHours} />}
            </div>
            <div className="rmatp-badges">
              <BadgeShelf path={`/api/admin/competitions/badges/teacher/${id}`} title="Competition badges" compact />
            </div>
          </div>
          <button type="button" className={`rmatp-btn ${active ? 'rmatp-btn--danger' : ''}`} onClick={() => { setStatusError(''); setConfirmOpen(true); }}>
            <FontAwesomeIcon icon={faPowerOff} /> {active ? 'Set inactive' : 'Set active'}
          </button>

          <ul className="rmatp-facts">
            <li>
              <FontAwesomeIcon icon={faEnvelope} />
              {teacher.Email ? <a href={`mailto:${teacher.Email}`}>{teacher.Email}</a> : <span className="rmatp-dim">No email</span>}
            </li>
            <li>
              <FontAwesomeIcon icon={faUser} />
              {teacher.Username || <span className="rmatp-dim">No username</span>}
            </li>
            <li>
              <FontAwesomeIcon icon={faWhatsapp} className="is-wa" />
              {digits(teacher.MobileNumber) ? (
                <a href={waUrl(teacher.MobileNumber)} target="_blank" rel="noopener noreferrer">
                  {teacher.MobileNumber}
                </a>
              ) : (
                <span className="rmatp-dim">No mobile</span>
              )}
            </li>
            <li>
              <FontAwesomeIcon icon={faCakeCandles} />
              {bdayOk ? (
                <span>
                  {bday.toLocaleDateString()} <span className="rmatp-dim">· {ageOf(bday)} years</span>
                </span>
              ) : (
                <span className="rmatp-dim">No birthday</span>
              )}
            </li>
          </ul>
        </header>

        {/* ---------- Tabs ---------- */}
        <div className="rmatp-bar">
          <div className="rmatp-tabs" role="tablist" aria-label="Teacher sections">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={activeTab === t.key}
                className={`rmatp-tab ${activeTab === t.key ? 'is-on' : ''}`}
                onClick={() => setTab(t.key)}
              >
                <FontAwesomeIcon icon={t.icon} />
                <span>{t.label}</span>
                {t.key === 'note' && teacher.Note && <i className="rmatp-dot" aria-label="has a note" />}
              </button>
            ))}
          </div>
        </div>

        {/* ---------- Content (same components + same props) ---------- */}
        <div className="rmatp-panel" role="tabpanel">
          {activeTab === 'calendar' && <LiveSchedule userData={userData} />}
          {activeTab === 'paid' && <PaidAvailability userData={userData} />}
          {activeTab === 'students' && <StudentsList userData={userData} />}
          {activeTab === 'earnings' && <Earnings userData={userData} />}
          {activeTab === 'pay' && <TeacherPay teacherId={id} />}
          {activeTab === 'tracking' && <UserTrackingTab data={tracking.data} loading={tracking.loading} userType="teacher" />}
          {activeTab === 'edit' && <EditTeacherInfo teacher={teacher} setTeacher={setTeacher} />}
          {activeTab === 'reset' && <ResetPassword teacherId={id} />}

          {activeTab === 'note' && (
            <section className="rmatp-card">
              <h2 className="rmatp-h2">
                <FontAwesomeIcon icon={faNoteSticky} /> Teacher’s note
              </h2>
              {teacher.Note ? (
                <>
                  <p className="rmatp-note">{teacher.Note}</p>
                  <p className="rmatp-meta">Written by the teacher on their dashboard.</p>
                </>
              ) : (
                <div className="rmatp-empty rmatp-empty--inline">
                  <FontAwesomeIcon icon={faNoteSticky} />
                  <p>This teacher hasn’t written a note yet.</p>
                </div>
              )}
            </section>
          )}
        </div>
      </div>

      {/* ---------- Confirm status ---------- */}
      {confirmOpen && (
        <div className="rmatp-overlay" onClick={() => !statusSaving && setConfirmOpen(false)}>
          <div className={`rmatp-dialog ${active ? 'is-bad' : 'is-good'}`} role="alertdialog" aria-modal="true" aria-label="Change status" onClick={(e) => e.stopPropagation()}>
            <span className="rmatp-dialog-icon">
              <FontAwesomeIcon icon={faPowerOff} />
            </span>
            <h2>{`Set ${firstName} as ${active ? 'inactive' : 'active'}?`}</h2>
            <p>{active ? 'They will move to the Inactive list.' : 'They will move back to the Active list.'}</p>
            {statusError && <p className="rmatp-error">{statusError}</p>}
            <div className="rmatp-dialog-actions">
              <button type="button" className="rmatp-btn rmatp-btn--ghost" onClick={() => setConfirmOpen(false)} disabled={statusSaving} autoFocus>
                Cancel
              </button>
              <button type="button" className={`rmatp-btn ${active ? 'rmatp-btn--solid-danger' : ''}`} onClick={changeStatus} disabled={statusSaving}>
                {statusSaving ? 'Saving…' : active ? 'Set inactive' : 'Set active'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="rmatp-toast" role="status">
          <FontAwesomeIcon icon={faCircleCheck} /> {toast}
        </div>
      )}
    </div>
  );
};

export default TeacherProfile;